/**
 * 任务服务
 * 处理图像生成任务相关的业务逻辑
 */

import type { HttpClient } from '../http/HttpClient'
import type { IStorage } from '../storage/Storage'
import type {
  TaskStatus,
  TaskListResponse,
  SubmitTaskParams,
  SubmitTaskResponse,
  TaskStatusResponse,
  SquareListParams,
  SquareListResponse
} from '../../types/task'
import { TaskAPI } from '../api/task'
import { BaseService } from './BaseService'
import { Polling } from '../utils/polling'

/** 任务轮询配置 */
export interface TaskPollingConfig {
  /** 初始间隔（毫秒），默认 1000 */
  initialInterval?: number
  /** 最大间隔（毫秒），默认 5000 */
  maxInterval?: number
  /** 超时时间（毫秒），默认 300000（5分钟） */
  timeout?: number
  /** 间隔增长因子，默认 1.5 */
  intervalFactor?: number
}

const DEFAULT_POLLING_CONFIG: Required<TaskPollingConfig> = {
  initialInterval: 1000,
  maxInterval: 5000,
  timeout: 300000,
  intervalFactor: 1.5
}

export class TaskService extends BaseService {
  private taskAPI: TaskAPI
  private pollingInstances: Map<string, Polling> = new Map()
  private pollingConfig: Required<TaskPollingConfig>

  constructor(httpClient: HttpClient, storage: IStorage, pollingConfig?: TaskPollingConfig) {
    super(httpClient, storage)
    this.taskAPI = new TaskAPI(httpClient)
    this.pollingConfig = { ...DEFAULT_POLLING_CONFIG, ...pollingConfig }
  }

  /**
   * 获取任务列表
   */
  async getTaskList(styleId: number, page = 1, limit = 10): Promise<TaskListResponse> {
    try {
      return await this.taskAPI.getTaskList(styleId, page, limit)
    } catch (error) {
      console.error('[TaskService] 获取任务列表失败:', error)
      throw error
    }
  }

  /**
   * 提交生成任务
   */
  async submitTask(params: SubmitTaskParams): Promise<SubmitTaskResponse> {
    try {
      return await this.taskAPI.submitTask(params)
    } catch (error) {
      console.error('[TaskService] 提交任务失败:', error)
      throw error
    }
  }

  /**
   * 获取任务状态
   */
  async getTaskStatus(serialNo: string): Promise<TaskStatusResponse> {
    try {
      return await this.taskAPI.getTaskStatus(serialNo)
    } catch (error) {
      console.error('[TaskService] 获取任务状态失败:', error)
      throw error
    }
  }

  /**
   * 轮询任务状态
   * @param serialNo 任务序列号
   * @param onStatusChange 状态变化回调
   * @param onProgress 进度回调
   * @returns Promise，任务完成时 resolve
   */
  async pollTaskStatus(
    serialNo: string,
    onStatusChange?: (status: TaskStatus, result?: TaskStatusResponse) => void,
    onProgress?: (progress: number) => void
  ): Promise<TaskStatusResponse> {
    // 停止之前的轮询
    this.stopPolling(serialNo)

    let currentInterval = this.pollingConfig.initialInterval

    const polling = new Polling({
      interval: currentInterval,
      timeout: this.pollingConfig.timeout,
      onPoll: (attempt) => {
        console.log(`[TaskService] 轮询任务状态, 第 ${attempt} 次`)
        // 动态增加间隔
        if (currentInterval < this.pollingConfig.maxInterval) {
          currentInterval = Math.min(
            currentInterval * this.pollingConfig.intervalFactor,
            this.pollingConfig.maxInterval
          )
        }
      }
    })

    this.pollingInstances.set(serialNo, polling)

    try {
      const result = await polling.start(
        async () => {
          const status = await this.taskAPI.getTaskStatus(serialNo)

          // 触发进度回调
          if (status.progress !== undefined && onProgress) {
            onProgress(status.progress)
          }

          // 触发状态变化回调
          if (onStatusChange) {
            onStatusChange(status.status, status)
          }

          return status
        },
        (status) => {
          // 任务完成或失败时停止轮询
          return status.status === 'success' || status.status === 'failed'
        }
      )

      return result
    } finally {
      this.pollingInstances.delete(serialNo)
    }
  }

  /**
   * 停止轮询
   */
  stopPolling(serialNo: string): void {
    const polling = this.pollingInstances.get(serialNo)
    if (polling) {
      polling.stop()
      this.pollingInstances.delete(serialNo)
    }
  }

  /**
   * 停止所有轮询
   */
  stopAllPolling(): void {
    this.pollingInstances.forEach((polling) => polling.stop())
    this.pollingInstances.clear()
  }

  /**
   * 删除任务
   */
  async deleteTask(serialNo: string): Promise<void> {
    try {
      // 先停止轮询
      this.stopPolling(serialNo)
      await this.taskAPI.deleteTask(serialNo)
    } catch (error) {
      console.error('[TaskService] 删除任务失败:', error)
      throw error
    }
  }

  /**
   * 推荐到广场
   */
  async recommendTask(taskId: string): Promise<void> {
    try {
      await this.taskAPI.recommendTask({ task_id: taskId })
    } catch (error) {
      console.error('[TaskService] 推荐任务失败:', error)
      throw error
    }
  }

  /**
   * 取消推荐
   */
  async cancelRecommend(taskId: string): Promise<void> {
    try {
      await this.taskAPI.cancelRecommend(taskId)
    } catch (error) {
      console.error('[TaskService] 取消推荐失败:', error)
      throw error
    }
  }

  /**
   * 获取广场列表
   */
  async getSquareList(params: SquareListParams): Promise<SquareListResponse> {
    try {
      return await this.taskAPI.getSquareList(params)
    } catch (error) {
      console.error('[TaskService] 获取广场列表失败:', error)
      throw error
    }
  }

  /**
   * 提交任务并轮询状态（组合操作）
   */
  async submitAndPoll(
    params: SubmitTaskParams,
    callbacks?: {
      onSubmitted?: (response: SubmitTaskResponse) => void
      onStatusChange?: (status: TaskStatus, result?: TaskStatusResponse) => void
      onProgress?: (progress: number) => void
    }
  ): Promise<TaskStatusResponse> {
    // 1. 提交任务
    const submitResult = await this.submitTask(params)
    callbacks?.onSubmitted?.(submitResult)

    // 2. 轮询状态
    return this.pollTaskStatus(
      submitResult.serial_no,
      callbacks?.onStatusChange,
      callbacks?.onProgress
    )
  }

  /**
   * 获取轮询配置
   */
  getPollingConfig(): Required<TaskPollingConfig> {
    return { ...this.pollingConfig }
  }

  /**
   * 更新轮询配置
   */
  setPollingConfig(config: TaskPollingConfig): void {
    this.pollingConfig = { ...this.pollingConfig, ...config }
  }
}
